import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Copy, X, Crown, LogOut, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed — select and copy manually"));
const day = (iso) => new Date(iso).toLocaleDateString();

export function CoachesPanel({ team }) {
  const { user, loadTeams } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [newLink, setNewLink] = useState("");
  const [confirm, setConfirm] = useState(null);
  const isOwner = team.can_manage;

  const load = useCallback(() => api.get(`/teams/${team.id}/coaches`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [team.id]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    try { const { data: d } = await api.post(`/teams/${team.id}/coach-invites`); setNewLink(`${window.location.origin}/coach-invite/${d.token}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async (id) => { try { await api.delete(`/teams/${team.id}/coach-invites/${id}`); toast.success("Invite canceled"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const removeCoach = async (m) => {
    try {
      await api.delete(`/teams/${team.id}/coaches/${m.id}`);
      if (m.id === user.id) { await loadTeams(); toast.success("You left the team"); nav("/teams"); return; }
      toast.success("Coach removed"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <p className="text-slate-400 text-sm">Loading coaches…</p>;

  return (
    <div className="space-y-4" data-testid="coaches-panel">
      <p className="text-sm text-slate-300">Assistant coaches can create, edit and duplicate plays. Only the head coach can delete plays, change team settings, manage sharing or invite coaches.</p>
      <ul className="space-y-2">
        {data.members.map((m) => (
          <li key={m.id} className="flex items-center gap-3 rounded-xl border border-[#1F3358] bg-[#0B1528] p-3" data-testid={`coach-member-${m.role}`}>
            {m.role === "owner" ? <Crown size={18} className="text-[#FACC15]" /> : <UserPlus size={18} className="text-slate-400" />}
            <div className="min-w-0 flex-1"><p className="font-bold truncate">{m.name || m.email}{m.id === user.id && " (you)"}</p><p className="text-xs text-slate-400 truncate">{m.email} · {m.role === "owner" ? "Head coach" : "Assistant coach"}</p></div>
            {m.role === "coach" && (isOwner || m.id === user.id) && (
              <button className="btn btn-danger px-3" onClick={() => setConfirm(m)} data-testid={m.id === user.id ? "btn-leave-team" : `btn-remove-coach-${m.id}`}>
                {m.id === user.id ? <><LogOut size={16} />Leave</> : <><UserMinus size={16} />Remove</>}
              </button>
            )}
          </li>
        ))}
      </ul>
      {isOwner && (
        <div className="card p-4 bg-[#0B1528] space-y-3">
          <p className="label">Invite an assistant coach</p>
          <p className="text-xs text-slate-400">Each link works once and expires in 7 days. The assistant signs in (or creates a coach account) and accepts.</p>
          {newLink && (
            <div className="space-y-2" data-testid="coach-invite-new">
              <input readOnly value={newLink} className="input font-mono text-xs" onFocus={(e) => e.target.select()} data-testid="coach-invite-link-input" />
              <button className="btn btn-primary" onClick={() => copy(newLink)} data-testid="btn-copy-coach-invite"><Copy size={16} />Copy invite link</button>
              <p className="text-xs text-[#FACC15]">Copy it now — for safety, this link is only shown once.</p>
            </div>
          )}
          <button className="btn btn-ghost" onClick={invite} data-testid="btn-create-coach-invite"><UserPlus size={16} />Create coach invite link</button>
          {data.invites.length > 0 && (
            <ul className="space-y-1.5" data-testid="coach-invites-list">
              {data.invites.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-2 text-sm text-slate-300">
                  <span>Open invite · created {day(i.created_at)} · expires {day(i.expires_at)}</span>
                  <button className="btn btn-ghost px-3 min-h-[36px]" onClick={() => cancel(i.id)} data-testid={`btn-cancel-invite-${i.id}`}><X size={14} />Cancel</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title={confirm?.id === user.id ? "Leave this team?" : "Remove this coach?"}
        body={confirm?.id === user.id ? "You'll lose access to this team's playbook." : `${confirm?.email} will no longer be able to open or edit this playbook.`}
        confirmLabel={confirm?.id === user.id ? "Leave team" : "Remove coach"} onConfirm={() => removeCoach(confirm)} testId="confirm-remove-coach" />
    </div>
  );
}
